<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <el-dialog
          :modal="true"
          :center="true"
          title="PDF Preview"
          :visible.sync="pdfPreview"
          :before-close="hidePdfPreview"
          width="70%"
        >
          <iframe :src="pdfUrl" class="pdf-preview-frame"></iframe>
          <span slot="footer" class="dialog-footer">
            <el-button @click="pdfPreview = false">Close</el-button>
          </span>
        </el-dialog>
        <el-dialog
          :modal="true"
          title="Download selected"
          :visible.sync="downloadModal"
          :before-close="hideDownloadModal"
        >
          <p>To send file to more than one email address, place a semicolon in-between each address.</p>
          <el-input
            type="input"
            placeholder="Please enter your email address"
            v-model="downloadEmail"
            clearable
          ></el-input>
          <span slot="footer" class="dialog-footer">
            <el-button @click="sendDownloadRequest">Download</el-button>
            <el-button @click="downloadModal = false">Close</el-button>
          </span>
        </el-dialog>
        <div class="d-flex flex-row-reverse" style="margin-bottom: 20px;">
          <el-button @click="downloadDialog">Download all that are checked</el-button>
        </div>
        <el-table
          v-loading="loading"
          :data="getPackageUsers"
          style="width: 100%"
          @selection-change="handleSelectionChange"
        >
          <el-table-column label="#" width="55">
            <template slot-scope="scope">{{ parseInt(scope.row.order, 10) + 1}}</template>
          </el-table-column>
          <el-table-column type="selection" width="55"></el-table-column>
          <el-table-column prop="username" label="Code" width="140"></el-table-column>
          <el-table-column label="Name" width="350">
            <template slot-scope="scope">
              <router-link
                :to="'/user/' + scope.row.general_id"
              >{{ displayName(scope.row.first_name, scope.row.last_name) }}</router-link>
            </template>
          </el-table-column>
          <el-table-column label="Status" width="120">
            <template slot-scope="scope">
              <i class="el-icon-pdf"></i>
              <span
                style="margin-left: 10px"
              >{{ fetchStatus(scope.row.reports, displayName(scope.row.first_name, scope.row.last_name)) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Date" width="240">
            <template slot-scope="scope">
              <i v-if="scope.row.create_date !== null" class="el-icon-time"></i>
              <span
                v-if="scope.row.create_date !== null"
                style="margin-left: 10px"
              >{{ formatDate(scope.row.create_date) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Notes">
            <template slot-scope="scope">
              <el-input
                type="text"
                placeholder="Custom notes"
                v-model="notes[scope.row.username]"
                @change="updateNotes(scope.row.username, notes[scope.row.username])"
              ></el-input>
            </template>
          </el-table-column>
          <el-table-column label="Operations" width="120">
            <template slot-scope="scope">
              <el-button size="mini" @click="viewProfile(scope.$index, scope.row)">View Profile</el-button>
            </template>
          </el-table-column>
        </el-table>
        <template>
          <div v-if="!loadComplete && loading" class="text-center">
            <i class="el-icon-loading"></i> Loading
          </div>
          <div v-if="loadComplete && !loading" class="text-center">All Records Shown :)</div>
        </template>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import axios from '../../shared/axios';
import { unserialize } from '../../shared/unserialize.js';
import { LANGUAGE_MAP } from '../../data/languageNames.js';

export default {
  name: 'tables',
  computed: {
    ...mapGetters('packages', [
      'getPackageList',
      'getPackageUsers',
      'getUserIdsForCurrent',
      'getPackageUsersPage',
      'getPackageUsersPageSize'
    ]),
    ...mapGetters('auth', ['getCompanyName'])
  },
  data: () => {
    return {
      loading: false,
      loadWait: false,
      loadComplete: false,
      pdfPreview: false,
      downloadModal: false,
      downloadEmail: '',
      downloadCodes: [],
      pdfUrl: '',
      langMap: {
        ...LANGUAGE_MAP
      },
      packageId: 0,
      page: 1,
      notes: []
    };
  },
  mounted() {
    window.addEventListener('scroll', this.scroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.scroll);
    this.clearSpliced();
    this.clearPackageUsers();
    this.setPackageViewPage(this.page);
  },
  created() {
    if (this.$route.params.packageId) {
      this.packageId = parseInt(this.$route.params.packageId, 0);
    }
    this.setPackageViewPage(this.page); // set the page back to 1

    const r = [];
    r.push(this.clearPackageUsers());
    r.push(this.clearSpliced());
    Promise.all(r).then(r => {
      this.fetchPackageList().then(() => {
        this.downloadData().then(() => {
          this.buildNotes();
        });
      });
    });
  },
  methods: {
    ...mapActions('packages', [
      'fetchPackageList',
      'fetchPackageDetails',
      'setCurrentPackage',
      'fetchPackageUser',
      'setPackageViewPage',
      'buildSpliced',
      'clearSpliced',
      'clearPackageUsers'
    ]),
    checkForReports(i) {
      console.log(i);
    },
    viewProfile(index, i) {
      this.$router.push('/user/' + i.general_id);
    },
    downloadData($state = undefined) {
      this.loading = true;
      return new Promise((resolve, reject) => {
        this.setCurrentPackage(this.packageId)
          .then(() => {
            return this.buildSpliced(this.page);
          })
          .then(page => {
            const userIds = this.getUserIdsForCurrent;

            const ep = [];
            for (const i in page) {
              const userId = parseInt(page[i], 10);
              if (!isNaN(userId)) {
                ep.push(this.fetchPackageUser({ userId: userId, order: i }));
              }
            }
            return Promise.all(ep).then(values => {
              this.loading = false;
              resolve({ success: 1, complete: page.length === userIds.length });
              return;
            });
          });
      });
    },
    scroll() {
      if (this.loadWait) return;

      let bottomOfWindow =
        document.body.scrollTop + window.innerHeight ===
        document.documentElement.offsetHeight;

      if (bottomOfWindow) {
        this.infiniteHandler();
        this.loadWait = true;
        setTimeout(() => (this.loadWait = false), 1000);
      }
    },
    relativeDate(input) {
      const a = moment();
      const b = moment(input);
      if (a.diff(b, 'years') > 1) {
        return moment(input).format('MMM Do YYYY');
      } else if (a.diff(b, 'years') === 1) {
        return moment(input).format('dddd, MMMM Do');
      } else {
        return moment(input, 'YYYYMMDD').fromNow();
      }
    },
    formatDate(input) {
      return moment(input).format('M/D/YYYY h:mm a z');
    },
    fetchStatus(ra, name) {
      if (ra === undefined) return '';
      if (ra.length > 0) {
        return 'Complete';
      }
      if (name !== '') {
        return 'Incomplete';
      }
      return 'Available';
    },
    fetchReports(reportsArray) {
      for (const i in reportsArray) {
        const reports = reportsArray[i].files;
        // https://spa.aai-live.com/pdf-download.php?ri=73101&ci=173849&rl=eng
      }
    },
    displayName(a, b) {
      if (b.trim().length === 0) {
        return a;
      }
      if (a.trim().length === 0) {
        return b;
      }
      const name = b + ', ' + a;
      if (name === 'Unknown, Unknown') return '';
      return name;
    },
    showPdfPriview(s) {
      this.pdfPreview = true;
      this.pdfUrl = s;
    },
    hidePdfPreview() {
      this.pdfPreview = false;
      this.pdfUrl = '';
    },
    buildNotes() {
      const newNotes = [];
      for (const inc in this.getPackageUsers) {
        if (this.getPackageUsers[inc].notes.trim().length > 0) {
          const username = this.getPackageUsers[inc].username;
          if (this.getPackageUsers[inc].notes.indexOf('\n') > -1) {
            const n = this.getPackageUsers[inc].notes.split('\n')[0];
            newNotes[username] = n;
          } else {
            newNotes[username] = this.getPackageUsers[inc].notes;
          }
        }
      }
      this.notes = Object.assign([], newNotes);
    },
    infiniteHandler($state) {
      // console.log('infiniteHandler');
      // console.log('Next Page: ' + this.page);
      this.page = this.getPackageUsersPage;
      this.page += 1;
      this.setPackageViewPage(this.page);
      this.downloadData()
        .then(r => {
          // console.log(r);
          this.buildNotes();
          if (r.complete) {
            // console.log('Calling -> $state.complete();');
            this.loadComplete = true;
            this.$message({
              message: 'All Results Loaded',
              type: 'success'
            });
          } else {
            this.$message({
              message: 'Finished loading section of 100',
              type: 'success'
            });
          }
        })
        .catch(e => {
          console.log('Hit error message');
          console.log(e);
          this.$message.error('Unable to download results');
        });
    },
    hideDownloadModal() {
      this.downloadEmail = '';
      this.downloadModal = false;
    },
    downloadDialog() {
      this.downloadEmail = '';
      this.downloadModal = true;
    },
    handleSelectionChange(v) {
      this.downloadCodes = [];
      for (const inc in v) {
        this.downloadCodes.push(v[inc].username);
      }
    },
    sendDownloadRequest() {
      axios
        .post('download/new', {
          codes: this.downloadCodes.join(','),
          email: this.downloadEmail,
          packageId: this.packageId
        })
        .then(r => {
          // console.log(r);
          this.downloadModal = false;
          this.$message({
            message:
              'Sent [' +
              this.downloadCodes.length +
              '] code(s) for download to: ' +
              this.downloadEmail,
            type: 'success'
          });
        })
        .catch(e => {
          console.error(e);
          this.$message.error('Unable to send download request');
        });
    },
    updateNotes(code, notes) {
      axios
        .post('/packages/notes/simple/' + code + '/', { notes: notes })
        .then(r => {
          console.log(r);
          this.$message({
            message: 'Saved Notes for: ' + code,
            type: 'success'
          });
        })
        .catch(e => {
          console.error(e);
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.report-link {
  margin-right: 30px;
}
.pdf-preview-frame {
  width: 100%;
  height: 65vh;
}
</style>

